/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC boundary; see mem://preferences/lint-debt.md */
import { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Tags, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { BUCKET_LABELS } from "@/lib/workspaceCorpus";
import { useWorkspace } from "@/hooks/useWorkspace";
import { tierCorpusDocumentCap } from "@/lib/tier";
import { CorpusDocumentEditor } from "./CorpusDocumentEditor";
import { CorpusDocumentList } from "./CorpusDocumentList";
import { CreateCorpusFolderDialog, RenameCorpusFolderDialog } from "./CorpusFolderDialogs";
import { PublishTypeChipSelector } from "../PublishTypeChipSelector";
import { useCorpusEmbedding } from "@/hooks/useCorpusEmbedding";
import { cn } from "@/lib/utils";
import { CorpusBucketSidebar } from "./CorpusBucketSidebar";
import { useCorpusDock } from "./useCorpusDock";

const LIST_SIZE_KEY = "excerptus.corpusDockListSize";

export const CorpusDockPanel = ({ workspaceId }: { workspaceId: string | undefined }) => {
  const ws = useWorkspace();
  const corpusCap = tierCorpusDocumentCap((ws as any).primaryTier ?? null);
  const dock = useCorpusDock(workspaceId);

  const [listSize, setListSize] = useState<number>(() => {
    if (typeof window === "undefined") return 28;
    const raw = window.localStorage.getItem(LIST_SIZE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n >= 18 && n <= 55 ? n : 28;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LIST_SIZE_KEY, String(listSize));
  }, [listSize]);

  // Auto re-embed the currently-edited doc 5 minutes after the last edit
  // when its embedding goes stale.
  useCorpusEmbedding(
    dock.editingDoc,
    dock.editingDoc?.updated_at ? new Date(dock.editingDoc.updated_at).getTime() : 0,
  );

  const { selection, folders, docs, publishTypes, editingDocId, editingDoc } = dock;
  const codexEnabled = ((ws.active as unknown as { codex_enabled?: boolean })?.codex_enabled) ?? false;

  /** Topic ids present on the currently-visible (pre-topic-filter) doc list,
   *  so the filter strip only offers topics that would actually narrow. */
  const availableTopicIds = useMemo(() => {
    const set = new Set<string>();
    for (const d of docs) {
      const ids = dock.docTopics[d.id] ?? [];
      for (const id of ids) set.add(id);
    }
    return Array.from(set);
  }, [docs, dock.docTopics]);

  const sortedAvailableTopicIds = useMemo(
    () =>
      [...availableTopicIds].sort((a, b) =>
        (dock.topicMeta[a]?.label ?? "").localeCompare(dock.topicMeta[b]?.label ?? "")
      ),
    [availableTopicIds, dock.topicMeta]
  );

  if (!workspaceId) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
        Select a workspace to use My Corpus.
      </div>
    );
  }

  const selectionTitle =
    selection.kind === "unfiled"
      ? "Unfiled"
      : selection.folderId
        ? folders.find((f) => f.id === selection.folderId)?.name ?? ""
        : `${BUCKET_LABELS[selection.bucket!]} (all)`;

  const totalDocs = docs.length;
  const overCap = corpusCap !== null && totalDocs >= corpusCap;



  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-full min-h-0 gap-2 p-2">
        <CorpusBucketSidebar
          workspaceId={workspaceId}
          selection={selection}
          setSelection={dock.setSelection}
          trees={dock.trees}
          docs={docs}
          publishTypes={publishTypes}
          anyTagged={dock.anyTagged}
          onSetPublishTypes={(rows) => dock.setPublishTypes(rows)}
          onCreateFolder={dock.openCreateFolder}
          onRenameRequest={(folder) => {
            dock.setRenameDialog({ open: true, folder });
            dock.setRenameValue(folder.name);
          }}
          onDeleteFolder={(folder) => void dock.handleDeleteFolder(folder)}
          onToggleGrounding={(folder, exclude) => void dock.handleToggleGrounding(folder, exclude)}
          onReorderFolder={(folderId, target) => {
            if (target.kind === "child") {
              void dock.handleReorderFolder({
                folderId,
                newParentId: target.parentId,
                bucket: target.bucket,
                newIndex: 0,
              });
            } else {
              void dock.handleReorderFolder({
                folderId,
                newParentId: target.parentId,
                bucket: target.bucket,
                newIndex: target.index,
              });
            }
          }}
          onMoveDocument={(documentId, folderId) =>
            void dock.handleReorderDocument({ documentId, newFolderId: folderId, newIndex: 0 })
          }
        />

        {/* Right: documents + editor */}
        <section className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <h3 className="truncate text-sm font-semibold">{selectionTitle}</h3>
              {corpusCap !== null && (
                <span
                  className={cn(
                    "shrink-0 rounded-full border px-1.5 py-0.5 text-[10px]",
                    overCap ? "border-destructive text-destructive" : "border-border text-muted-foreground"
                  )}
                  title="Corpus document cap for your tier"
                >
                  {totalDocs}/{corpusCap}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {dock.staleCount > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline" onClick={() => void dock.handleReembedStale()}>
                      <RefreshCw className="mr-1 h-3.5 w-3.5" /> Re-embed stale ({dock.staleCount})
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Re-embed all documents whose body changed since their last embedding.
                  </TooltipContent>
                </Tooltip>
              )}
              <Button size="sm" onClick={() => dock.startNewDoc()} disabled={overCap}>
                <Plus className="mr-1 h-3.5 w-3.5" /> New
              </Button>
            </div>
          </div>

          <ResizablePanelGroup
            direction="horizontal"
            className="min-h-0 flex-1"
            onLayout={(sizes) => {
              if (sizes[0] !== undefined) setListSize(sizes[0]);
            }}
          >
            {/* Document list */}
            <ResizablePanel defaultSize={listSize} minSize={18} maxSize={55}>
              <div className="flex h-full flex-col overflow-hidden rounded-md border border-border bg-card">
                {codexEnabled && sortedAvailableTopicIds.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 border-b border-border px-1.5 py-1">
                    <Tags className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                    {sortedAvailableTopicIds.map((tid) => {
                      const label = dock.topicMeta[tid]?.label ?? "Topic";
                      const active = dock.topicFilter.includes(tid);
                      return (
                        <button
                          key={tid}
                          type="button"
                          onClick={() =>
                            dock.setTopicFilter((prev) =>
                              prev.includes(tid) ? prev.filter((x) => x !== tid) : [...prev, tid]
                            )
                          }
                          className={cn(
                            "rounded-full border px-1.5 py-0 text-[10px] leading-5 transition-colors",
                            active
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-muted/40 text-muted-foreground hover:bg-muted"
                          )}
                          aria-pressed={active}
                        >
                          {label}
                        </button>
                      );
                    })}
                    {dock.topicFilter.length > 0 && (
                      <button
                        type="button"
                        onClick={() => dock.setTopicFilter([])}
                        className="ml-auto inline-flex items-center gap-0.5 rounded px-1 py-0 text-[10px] leading-5 text-muted-foreground hover:bg-muted"
                        aria-label="Clear topic filter"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto p-1">
                  <CorpusDocumentList
                    loading={dock.loading}
                    visibleDocs={dock.visibleDocs}
                    editingDocId={editingDocId}
                    onOpen={(doc) => void dock.openExistingDoc(doc)}
                    onReembed={(docId) => void dock.handleReembed(docId)}
                    onDelete={(doc) => void dock.handleDeleteDoc(doc)}
                    docTopics={dock.docTopics}
                    topicMeta={dock.topicMeta}
                    showTopicChips={codexEnabled}
                    workspaceId={workspaceId}
                    onSetDocumentTopics={(docId, next) => void dock.setRowTopics(docId, next)}
                    onReorderDocument={(documentId, targetIndex) => {
                      const currentFolderId =
                        selection.kind === "folder" ? selection.folderId : null;
                      void dock.handleReorderDocument({
                        documentId,
                        newFolderId: currentFolderId,
                        newIndex: targetIndex,
                      });
                    }}
                  />
                </div>
              </div>
            </ResizablePanel>


            <ResizableHandle withHandle className="mx-1 bg-transparent" />

            {/* Editor */}
            <ResizablePanel defaultSize={100 - listSize} minSize={45}>
              <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-md border border-border bg-card">
                {editingDoc && (
                  <div className="flex items-center justify-end border-b border-border px-2 py-1">
                    <PublishTypeChipSelector
                      documentId={editingDoc.id}
                      workspaceId={workspaceId}
                      publishTypes={publishTypes}
                    />
                  </div>
                )}
                <div className="min-h-0 flex-1 overflow-hidden p-2">
                  {editingDocId ? (
                    <CorpusDocumentEditor
                      key={editingDocId ?? "empty"}
                      initialTitle={dock.editorTitle}
                      initialBody={dock.editorBody}
                      initialTopicIds={dock.editorTopicIds}
                      codexEnabled={codexEnabled}
                      busy={dock.editorBusy}
                      workspaceId={workspaceId}
                      onSave={dock.saveEditor}
                      onCancel={() => dock.setEditingDocId(null)}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center p-4 text-center text-xs text-muted-foreground">
                      Select a document to edit or click <span className="ml-1 font-medium">New</span>.
                    </div>
                  )}
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </section>

        <CreateCorpusFolderDialog
          state={dock.folderDialog}
          onOpenChange={(o) => dock.setFolderDialog((s) => ({ ...s, open: o }))}
          name={dock.folderDialogName}
          onNameChange={dock.setFolderDialogName}
          onBucketChange={(b) => dock.setFolderDialog((s) => ({ ...s, bucket: b }))}
          onSubmit={() => void dock.submitCreateFolder()}
        />

        <RenameCorpusFolderDialog
          open={dock.renameDialog.open}
          folder={dock.renameDialog.folder}
          value={dock.renameValue}
          onValueChange={dock.setRenameValue}
          onClose={() => dock.setRenameDialog({ open: false, folder: null })}
          onSubmit={() => void dock.submitRename()}
        />
      </div>
    </TooltipProvider>
  );
};

export default CorpusDockPanel;
